import { useState } from 'react';
import {
  Webhook,
  RefreshCw,
  Loader2,
  Zap,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useWebhooksQuery } from '@/client/api/workflows/workflows.query';
import { useWebhookTestFireMutation } from '@/client/api/workflows/workflows.mutation';
import { WebhookRoute } from '@/client/types/workflows';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  WorkflowAccessDenied,
  getApiErrorCode,
} from '@/client/components/admin/Workflows/workflowUi';

const WebhooksPage = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Workflows', href: '/admin/workflows' },
    { label: 'Webhooks' },
  ]);

  const viewPerm = useHasPermission('workflow.view');
  const managePerm = useHasPermission('workflow.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { data, isLoading, isError, error, refetch, isFetching } = useWebhooksQuery(canView);
  const testFire = useWebhookTestFireMutation();

  const [selected, setSelected] = useState<WebhookRoute | null>(null);
  const [payloadJson, setPayloadJson] = useState('{\n  "eid": 7895\n}');
  const [firingRoute, setFiringRoute] = useState<string | null>(null);

  const handleTestFire = async (route: string) => {
    if (!canManage) return;
    let payload: Record<string, unknown> | undefined;
    try {
      const parsed = JSON.parse(payloadJson || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        payload = parsed as Record<string, unknown>;
      } else {
        toast.error('Payload must be a JSON object', { richColors: true });
        return;
      }
    } catch {
      toast.error('Invalid JSON payload', { richColors: true });
      return;
    }

    setFiringRoute(route);
    try {
      const res = await testFire.mutateAsync({
        route,
        body: { payload },
      });
      toast.success(`Test-fire enqueued (${res.idem})`, {
        richColors: true,
        description: res.note,
      });
    } catch (err) {
      const code = getApiErrorCode(err);
      toast.error(
        code === 'route_disabled'
          ? 'Route is disabled (409 route_disabled)'
          : getApiErrorMessage(err, 'Test-fire failed'),
        { richColors: true },
      );
    } finally {
      setFiringRoute(null);
    }
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <WorkflowAccessDenied />
        </div>
      </div>
    );
  }

  const routes = data?.routes ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Webhook className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Outbound Webhooks</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                Signed webhook route registry. Secrets are truncated; never returned in full.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
        </div>

        {isError && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {getApiErrorMessage(error, 'Failed to load webhook routes')}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {isLoading ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : routes.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-gray-400">No routes registered.</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {routes.map((r) => (
                  <li key={r.route}>
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      className={`flex w-full items-start justify-between gap-3 px-4 py-4 text-left hover:bg-gray-50 ${
                        selected?.route === r.route ? 'bg-slate-50' : ''
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">{r.route}</span>
                          <Badge
                            variant="outline"
                            className={
                              r.enabled
                                ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                                : 'border-gray-200 bg-gray-50 text-gray-600'
                            }
                          >
                            {r.enabled ? 'enabled' : 'disabled'}
                          </Badge>
                          {r.engine && (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                              {r.engine}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 truncate text-xs text-gray-500">{r.url ?? '—'}</p>
                        <p className="mt-1 text-xs text-gray-400">
                          {r.domain ?? '—'} · secret{' '}
                          {r.secret_present ? (
                            <span className="text-emerald-600 inline-flex items-center gap-0.5">
                              <CheckCircle2 className="h-3 w-3" /> present
                            </span>
                          ) : (
                            <span className="text-red-600 inline-flex items-center gap-0.5">
                              <XCircle className="h-3 w-3" /> missing
                            </span>
                          )}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            {!selected ? (
              <p className="py-10 text-center text-sm text-gray-400">
                Select a route to inspect and test-fire.
              </p>
            ) : (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{selected.route}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {selected.engine} · {selected.domain}
                  </p>
                </div>

                <dl className="space-y-2 text-sm">
                  <Detail label="URL" value={selected.url ?? '—'} mono />
                  <Detail label="URL env" value={selected.url_env ?? '—'} mono />
                  <Detail label="Enabled env" value={selected.enabled_env ?? '—'} mono />
                  <Detail label="Secret env" value={selected.secret_env ?? '—'} mono />
                  <Detail label="Secret preview" value={selected.secret_preview ?? '—'} mono />
                  <Detail label="Sig header" value={selected.sig_header ?? '—'} mono />
                  <Detail label="Timestamp header" value={selected.ts_header ?? '—'} mono />
                  <Detail label="Nonce header" value={selected.nonce_header ?? '—'} mono />
                </dl>

                {canManage && (
                  <div className="space-y-2 border-t border-gray-100 pt-4">
                    <label className="text-xs font-medium text-gray-500">
                      Extra payload (merged into synthetic test body)
                    </label>
                    <textarea
                      value={payloadJson}
                      onChange={(e) => setPayloadJson(e.target.value)}
                      rows={5}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs outline-none focus:border-gray-400"
                    />
                    <Button
                      size="sm"
                      className="gap-2"
                      disabled={firingRoute === selected.route || !selected.enabled}
                      onClick={() => handleTestFire(selected.route)}
                    >
                      {firingRoute === selected.route ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Zap className="h-4 w-4" />
                      )}
                      Test-fire
                    </Button>
                    {!selected.enabled && (
                      <p className="text-xs text-amber-700">
                        Route is disabled — test-fire returns 409 route_disabled.
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      Payload is tagged <code>admin_test_fire</code>,{' '}
                      <code>suppress_side_effects</code>, and <code>dry_run</code>.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Detail = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div className="grid grid-cols-[120px_1fr] gap-2">
    <dt className="text-gray-500">{label}</dt>
    <dd className={`break-all text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
  </div>
);

export default WebhooksPage;
